import { Instance, SnapshotOut, types } from "mobx-state-tree"
import { Toast } from "native-base";
import { Platform } from "react-native";
import {
  AutoFeederPond,
  Batch,
  Cycle,
  CycleToDo,
  FarmUser,
  Measurement,
  MultiMeasurement,
  Parameter,
  Pond, Product,
  Stock,
  User,
} from "../../../database"
import { PondStoreModel } from "./pond-store/pond-store";
import { AutoFeederPondStoreModel } from "./pond-store/auto-feeder-pond-store/auto-feeder-pond-store"
import { CycleStoreModel } from "./pond-store/cycle-store/cycle-store";
import { BatchStoreModel } from "./batch-store/batch-store";
import _ from "lodash";
import moment from "moment"
import { createAxios } from "../../../utils/axios"
import { IAnyStateTreeNode } from "mobx-state-tree/dist/internal"
import { withRootStore } from "../../extensions/with-root-store"
import { withEnvironment } from "../../extensions/with-environment"
import { withBaseStoreModels } from "../../extensions/with-base-store-models"

const FarmStoreProps = {
  farmId: types.maybeNull(types.number),
  farm: types.maybeNull(types.frozen()),
  pondId: types.maybeNull(types.number),
  pondStoreModels: types.optional(types.array(PondStoreModel), []),
  proPonds: types.optional(types.array(types.frozen()), []),
  rfidables: types.optional(types.array(types.frozen()), []),
  batchId: types.maybeNull(types.string),
  batchStoreModels: types.optional(types.array(BatchStoreModel), []),
  userId: types.maybeNull(types.number),
  users: types.optional(types.array(types.frozen()), []),
  farmShrimpPriceId: types.maybeNull(types.number),
  farmShrimpPrices: types.optional(types.array(types.frozen()), []),
  stockId: types.maybeNull(types.number),
  stocks: types.optional(types.array(types.frozen()), []),
  parameter: types.optional(types.array(types.frozen()), []),
  planktonParameter: types.optional(types.array(types.frozen()), []),
  microbioParameter: types.optional(types.array(types.frozen()), []),

  initiating: types.maybeNull(types.boolean),

  queryingPond: types.maybeNull(types.boolean),
  lastUpdatePond: types.maybeNull(types.string),
  syncingPond: types.maybeNull(types.boolean),
  syncedPond: types.maybeNull(types.boolean),
  queryingProPond: types.maybeNull(types.boolean),
  queryingRfidable: types.maybeNull(types.boolean),
  queryingCycle: types.maybeNull(types.boolean),
  lastUpdateCycle: types.maybeNull(types.string),
  syncingCycle: types.maybeNull(types.boolean),
  syncedCycle: types.maybeNull(types.boolean),
  queryingAutoFeederPond: types.maybeNull(types.boolean),
  syncingAutoFeederPond: types.maybeNull(types.boolean),
  syncedAutoFeederPond: types.maybeNull(types.boolean),
  queryingWaterQualityMultiMeasurement: types.maybeNull(types.boolean),
  syncingWaterQualityMultiMeasurement: types.maybeNull(types.boolean),
  syncedWaterQualityMultiMeasurement: types.maybeNull(types.boolean),
  queryingCycleToDo: types.maybeNull(types.boolean),
  lastUpdateCycleToDo: types.maybeNull(types.string),
  syncingCycleToDo: types.maybeNull(types.boolean),
  syncedCycleToDo: types.maybeNull(types.boolean),
  queryingBatch: types.maybeNull(types.boolean),
  syncingBatch: types.maybeNull(types.boolean),
  syncedBatch: types.maybeNull(types.boolean),
  queryingUser: types.maybeNull(types.boolean),
  syncingUser: types.maybeNull(types.boolean),
  syncedUser: types.maybeNull(types.boolean),
  queryingFarmShrimpPrice: types.maybeNull(types.boolean),
  queryingStock: types.maybeNull(types.boolean),
  syncingStock: types.maybeNull(types.boolean),
  syncedStock: types.maybeNull(types.boolean),
}

/**
 * Model description here for TypeScript hints.
 */
export const FarmStoreModel = types
  .model("FarmStore")
  .props(FarmStoreProps)
  .extend(withRootStore)
  .extend(withEnvironment)
  .extend(withBaseStoreModels)
  .views((self) => ({
    get pondStore() {
      return self.pondStoreModels.find((pondStore) => pondStore.pondId === self.pondId)
    },
    get ponds() {
      return self.pondStoreModels.map((pondStore) => pondStore.pond)
    },
    get activePondStores() {
      return self.pondStoreModels.filter((pondStore) => pondStore.activeCycle)
    },
    get inactivePondStores() {
      return self.pondStoreModels.filter((pondStore) => !pondStore.activeCycle)
    },
    get inactivePonds() {
      return this.inactivePondStores.map((pondStore) => pondStore.pond)
    },
    get cycleStoreModels() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycleStoreModels)
    },
    get cycles() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycles)
    },
    get autoFeederPonds() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.autoFeederPonds)
    },
    get waterQualityMultiMeasurements() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycleStoreModels.flatMap((cycleStore) => cycleStore.waterQualityMultiMeasurements))
    },
    get feeds() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycleStoreModels.flatMap((cycleStore) => cycleStore.feeds))
    },
    get feedingTrayControls() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycleStoreModels.flatMap((cycleStore) => cycleStore.feedingTrayControls))
    },
    get samplings() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycleStoreModels.flatMap((cycleStore) => cycleStore.samplings))
    },
    get treatments() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycleStoreModels.flatMap((cycleStore) => cycleStore.treatments))
    },
    get harvests() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycleStoreModels.flatMap((cycleStore) => cycleStore.harvests))
    },
    get mortalities() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycleStoreModels.flatMap((cycleStore) => cycleStore.mortalities))
    },
    get chemicalMultiMeasurements() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycleStoreModels.flatMap((cycleStore) => cycleStore.chemicalMultiMeasurements))
    },
    get planktonMultiMeasurements() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycleStoreModels.flatMap((cycleStore) => cycleStore.planktonMultiMeasurements))
    },
    get microbiologyMultiMeasurements() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycleStoreModels.flatMap((cycleStore) => cycleStore.microbiologyMultiMeasurements))
    },
    get cycleDiseases() {
      return self.pondStoreModels.flatMap((pondStore) => pondStore.cycleStoreModels.flatMap((cycleStore) => cycleStore.cycleDiseases))
    },
    get batchStore() {
      return self.batchStoreModels.find((batchStore) => batchStore.batchId === self.batchId)
    },
    get batches() {
      return self.batchStoreModels.map((batchStore) => batchStore.batch)
    },
    get lastBatchStore() {
      return self.batchStoreModels.find((batchStore) => batchStore)
    },
    get activeBatchStore() {
      return self.batchStoreModels.find((batchStore) => batchStore.isActive)
    },
    get lastFarmShrimpPrice() {
      return self.farmShrimpPrices.find((farmShrimpPrice) => farmShrimpPrice)
    },
    get loadingPond() {
      return self.queryingPond || self.syncingPond
    },
    get loadingCycle() {
      return self.queryingCycle || self.syncingCycle
    },
    get loadingAutoFeederPond() {
      return self.queryingAutoFeederPond || self.syncingAutoFeederPond
    },
    get loadingWaterQualityMultiMeasurement() {
      return self.queryingWaterQualityMultiMeasurement || self.syncingWaterQualityMultiMeasurement
    },
    get loadingCycleToDo() {
      return self.queryingCycleToDo || self.syncingCycleToDo
    },
    get loadingBatch() {
      return self.queryingBatch || self.syncingBatch || (this.batchStore && this.batchStore.syncingCycleActualAndPrediction)
    },
    get loadingPondCycle() {
      return self.pondStoreModels.reduce((previousValue, pondStore) => previousValue || pondStore.loadingCycle, false)
    },
    get loadingPondAutoFeederPond() {
      return self.pondStoreModels.reduce((previousValue, pondStore) => previousValue || pondStore.loadingAutoFeederPond, false)
    },
  })) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof FarmStoreProps, value: any) {
      if (self[key] === value) return;
      self[key] = value as never;
    },
    setArrayModelStore(
      key: "pondStoreModels"|"batchStoreModels",
      values: Array<any>,
      modelId: string, model: string, modelKey: string,
      defaultStore: (props?: any) => IAnyStateTreeNode
    ) {
      return self.setArrayBaseModelStore(
        key, values, modelId, model, modelKey, defaultStore
      )
    },
    async getPonds(sync = true, onFinishSync = undefined) {
      return self.getModels(
        Pond,
        'queryingPond', 'syncingPond', 'syncedPond',
        { farm_id__eq: self.farmId, sort: 'name_alpha_numeric' },
        async (models) => {
          await this.getPondsExtended();
          this.setArrayModelStore(
            "pondStoreModels",
            models.map((model: Pond) => {
              const rfidables = self.rfidables.filter((r) => String(r.rfidable_id) === String(model.getKey()));
              return {
                ...model.toArray(),
                pro: self.proPonds.find((proPond) => String(proPond.id) === String(model.getKey())) ? true : undefined,
                rfid: (rfidables[0] || {}).rfid_id || null,
                rfidables
              }
            }),
            'pondId', 'pond', 'id',
            (props = {}) => PondStoreModel.create(props)
          )
          this.set('lastUpdatePond', `${_.max(models.map((m: Pond) => m.getAttributes().updated_at)) || ''}-${models.length}`)
          if (!models.find((model) => model.getKey() === self.pondId)) {
            this.set('pondId', models[0] && models[0].getKey() || null)
          }
        },
        sync, async () => this.syncPonds(), onFinishSync
      )
    },
    async syncPonds() {
      if (Platform.OS === 'web') return true;

      this.set('syncingPond', true)
      Pond.endpoint = `/farms/${self.farmId}/ponds`
      const success = await Pond.syncAll(
        { farm_id__in: self.farmId },
        [['ponds.farm_id', [self.farmId]]], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )
      Pond.endpoint = `/ponds`

      this.set('syncingPond', false)
      return success
    },
    async getCycles(sync = true, onFinishSync = undefined) {
      const pondIds = self.pondStoreModels.map((pondStore) => pondStore.pondId);
      return self.getModels(
        Cycle,
        'queryingCycle', 'syncingCycle', 'syncedCycle',
        { pond_id__in: pondIds.join(','), sort: 'started_at,desc|pond_name_alpha_numeric' },
        async (models) => {
          const cyclesPerPond = _.groupBy(models, (cycle: Cycle) => cycle.getAttribute('pond_id'));
          self.pondStoreModels.forEach((pondStore) => {
            pondStore.setArrayModelStore(
              'cycleStoreModels',
              (cyclesPerPond[pondStore.pondId] || []).map((cycle) => ({
                ...cycle.toArray(),
                pro: pondStore.pond.pro || cycle.getAttribute('pro')
              })),
              'cycleId', 'cycle', 'id',
              () => CycleStoreModel.create({})
            )
            pondStore.set('lastUpdateCycle', `${_.max((cyclesPerPond[pondStore.pondId] || []).map((m: Cycle) => m.getAttributes().updated_at))}-${(cyclesPerPond[pondStore.pondId] || []).length}`)
          })
          this.set('lastUpdateCycle', `${_.max(models.map((m: Cycle) => m.getAttributes().updated_at))}-${models.length}`)
        },
        sync, async () => this.syncCycles(), () => {
          onFinishSync && onFinishSync();
          self.pondStoreModels.forEach((pondStore) => {
            pondStore.set('syncedCycle', true);
          });
        }
      )
    },
    async syncCycles() {
      if (Platform.OS === 'web') return true;

      this.set('syncingCycle', true)
      const pondIds = self.pondStoreModels.map((pondStore) => pondStore.pondId);
      const success = await Cycle.syncAll(
        { pond_id__in: pondIds.join(',') },
        [['cycles.pond_id', pondIds]], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )

      this.set('syncingCycle', false)
      return success
    },
    async getAutoFeederPonds(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingAutoFeederPond', true);
        const pondIds = self.pondStoreModels.map((pondStore) => pondStore.pondId);
        const autoFeederPonds = await AutoFeederPond.fetch({ pond_id__in: pondIds.join(','), sort: 'name,asc|name,asc' }).catch((e) => {
          console.warn(e)
          return []
        })
        const autoFeederPondsPerPond = _.groupBy(autoFeederPonds, (autoFeederPond: AutoFeederPond) => autoFeederPond.getAttribute('pond_id'));
        self.pondStoreModels.forEach((pondStore) => {
          pondStore.setArrayModelStore(
            'autoFeederPondStoreModels',
            (autoFeederPondsPerPond[pondStore.pondId] || []).map((autoFeederPond) => autoFeederPond.toArray()),
            'autoFeederPondId', 'autoFeederPond', 'id',
            () => AutoFeederPondStoreModel.create({})
          )
        })
        this.set('queryingAutoFeederPond', false);
      }
      else {
        this.set('queryingAutoFeederPond', true);
        this.syncAutoFeederPonds().then(async () => {
          await this.getAutoFeederPonds(false).catch()
          onFinishSync && onFinishSync()
          self.pondStoreModels.forEach((pondStore) => {
            pondStore.set('syncedAutoFeederPond', true)
          })
          this.set('syncedAutoFeederPond', true);
          this.set('queryingAutoFeederPond', false);
        })
      }
    },
    async syncAutoFeederPonds() {
      if (Platform.OS === 'web') return true;

      this.set('syncingAutoFeederPond', true)
      const pondIds = self.pondStoreModels.map((pondStore) => pondStore.pondId);
      const success = await AutoFeederPond.syncAll(
        { pond_id__in: pondIds.join(',') },
        [['auto_feeder_ponds.pond_id', pondIds]], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )

      this.set('syncingAutoFeederPond', false)
      return success
    },
    async getWaterQualityMultiMeasurements(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingWaterQualityMultiMeasurement', true);
        const activePondStores = self.activePondStores;
        const pondIds = activePondStores.map((pondStore) => pondStore.pondId);
        const cycleIds = activePondStores.map((pondStore) => pondStore.activeCycleStoreModel.cycleId);
        MultiMeasurement.parameters = await Parameter.fetch({ parameter_category_id__eq: 1, sort: 'number' }).catch(() => [])
        const parameterIds = MultiMeasurement.parameters.map((parameter) => parameter.getKey())
        const waterQualityMultiMeasurements = await MultiMeasurement.fetch({
          pond_id__in: pondIds.join(','),
          cycle_id__in: cycleIds.join(','),
          parameter_id__in: parameterIds.join(','),
          sort: 'measured_at,desc'
        }).catch((e) => {
          console.warn(e)
          return []
        })
        MultiMeasurement.parameters = Measurement.parameters
        const waterQualityMultiMeasurementsPerCycle = _.groupBy(waterQualityMultiMeasurements, (waterQualityMultiMeasurement: MultiMeasurement) => waterQualityMultiMeasurement.getAttribute('pond_id'));
        self.pondStoreModels.forEach((pondStore) => {
          const cycleStore = pondStore.activeCycleStoreModel;
          if (cycleStore)
            cycleStore.set('waterQualityMultiMeasurements', (
                (waterQualityMultiMeasurementsPerCycle[pondStore.pondId] || []).map((waterQualityMultiMeasurement) => waterQualityMultiMeasurement.toArray())
            ))
        })
        this.set('queryingWaterQualityMultiMeasurement', false);
      }
      else {
        this.set('queryingWaterQualityMultiMeasurement', true);
        this.syncWaterQualityMultiMeasurementsLastWeek().then(async () => {
          await this.getWaterQualityMultiMeasurements(false).catch()
          onFinishSync && onFinishSync()
          self.pondStoreModels.forEach((pondStore) => {
            const cycleStore = pondStore.activeCycleStoreModel;
            if (cycleStore) cycleStore.set('syncedWaterQualityMultiMeasurement', true)
          })
          this.set('syncedWaterQualityMultiMeasurement', true);
          this.set('queryingWaterQualityMultiMeasurement', false);
        })
      }
    },
    async syncWaterQualityMultiMeasurementsLastWeek() {
      if (Platform.OS === 'web') return true;

      this.set('syncingWaterQualityMultiMeasurement', true)
      const activePondStores = self.activePondStores;
      const pondIds = activePondStores.map((pondStore) => pondStore.pondId);
      const cycleIds = activePondStores.map((pondStore) => pondStore.activeCycleStoreModel.cycleId);
      Measurement.parameters = await Parameter.fetch({ parameter_category_id__eq: 1, sort: 'number' }).catch(() => [])
      const parameterIds = Measurement.parameters.map((parameter) => parameter.getKey())
      const success = await Measurement.syncAll(
        {
          pond_id__in: pondIds.join(','),
          cycle_id__in: cycleIds.join(','),
          parameter_id__in: parameterIds.join(','),
          measured_at__gte: moment().subtract(1, 'week').format('YYYY-MM-DD')
        },
        [['measurements.cycle_id', cycleIds]], false, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )
      Measurement.parameters = [...Measurement.originalParameters]

      this.set('syncingWaterQualityMultiMeasurement', false)
      return success
    },
    async getCycleToDos(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingCycleToDo', true);
        const cycleIds = self.pondStoreModels.map((pondStore) => (pondStore.activeCycleStoreModel || {}).cycleId).filter((id) => id);
        await CycleToDo.fetch({
          cycle_id__in: cycleIds.join(','),
          sort: 'logged_at,desc'
        }).then((cycleToDos) => {
          const cycleToDosPerCycle = _.groupBy(cycleToDos, (cycleToDo: CycleToDo) => cycleToDo.getAttribute('pond_id'));
          self.pondStoreModels.forEach((pondStore) => {
            const cycleStore = pondStore.activeCycleStoreModel;
            if (cycleStore)
              cycleStore.set('cycleToDos', (
                (cycleToDosPerCycle[pondStore.pondId] || []).map((cycleToDo) => cycleToDo.toArray())
              ))
          })
          this.set('lastUpdateCycleToDo', `${_.max(cycleToDos.map((cycleToDo: CycleToDo) => cycleToDo.getAttributes().updated_at)) || ''}-${cycleToDos.length}`);
        }).catch((e) => {
          console.warn(e)
          return []
        })
        this.set('queryingCycleToDo', false);
      }
      else {
        this.set('queryingCycleToDo', true);
        this.syncCycleToDos().then(async () => {
          await this.getCycleToDos(false).catch()
          onFinishSync && onFinishSync()
          self.pondStoreModels.forEach((pondStore) => {
            const cycleStore = pondStore.activeCycleStoreModel;
            if (cycleStore) cycleStore.set('syncedCycleToDo', true)
          })
          this.set('syncedCycleToDo', true);
          this.set('queryingCycleToDo', false);
        })
      }
    },
    async syncCycleToDos() {
      if (Platform.OS === 'web') return true;

      this.set('syncingCycleToDo', true)
      const cycleIds = self.pondStoreModels.map((pondStore) => (pondStore.activeCycleStoreModel || {}).cycleId).filter((id) => id);
      const success = await CycleToDo.syncAll(
        { cycle_id__in: cycleIds.join(',') },
        [['cycle_to_dos.cycle_id', cycleIds]], false, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )

      this.set('syncingCycleToDo', false)
      return success
    },
    async getBatches(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingBatch', true);
        const batches = await Batch.fetch({ farm_id__eq: self.farmId, sort: 'start_batch,desc' }).catch((e) => {
          console.warn(e)
          return []
        })
        this.setArrayModelStore(
          "batchStoreModels",
          batches.map((b) => b.toArray()),
          'batchId', 'batch', 'id',
          (props = {}) => BatchStoreModel.create(props)
        )
        if (!batches.find((batch) => batch.getKey() === self.batchId)) {
          this.set('batchId', batches[0] && batches[0].getKey() || null)
        }
        this.set('queryingBatch', false);
      }
      else {
        this.set('queryingBatch', true);
        this.syncBatches().then(async () => {
          await this.getBatches(false).catch()
          onFinishSync && onFinishSync()
          this.set('syncedBatch', true);
          this.set('queryingBatch', false);
        })
      }
    },
    async syncBatches() {
      if (Platform.OS === 'web') return true;

      this.set('syncingBatch', true)
      const success = !!self.ponds.length && await Cycle.syncAll(
        { pond_id__in: self.ponds.map((p) => p.id).join(',') },
        [['cycles.pond_id', self.ponds.map((p) => p.id)]], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )

      this.set('syncingBatch', false)
      return success
    },
    async getUsers(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingUser', true);
        const farmUsers = await FarmUser.fetch({
          farm_id__in: self.farmId
        }).catch((e) => {
          console.warn(e)
          return []
        })
        const userIds = farmUsers.map((farmUser) => farmUser.user_id)
        const users = await User.fetch({
          id__in: userIds.join(',')
        }).catch((e) => {
          console.warn(e)
          return []
        })
        this.set('users', users.map((user) => user.toArray()));
        this.set('queryingUser', false);
      }
      else {
        this.set('queryingUser', true);
        this.syncUsers().then(async () => {
          await this.getUsers(false).catch()
          onFinishSync && onFinishSync()
          this.set('syncedUser', true);
          this.set('queryingUser', false);
        })
      }
    },
    async syncUsers() {
      if (Platform.OS === 'web') return true;

      this.set('syncingUser', true)
      const farmUsers = await FarmUser.fetch({
        farm_id__in: self.farmId
      }).catch((e) => {
        console.warn(e)
        return []
      })
      const userIds = farmUsers.map((farmUser) => farmUser.user_id)
      const success = await User.syncAll(
        { id__in: userIds.join(',') },
        [['users.id', userIds]], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )

      this.set('syncingUser', false)
      return success
    },
    async getFarmShrimpPrices() {
      this.set('queryingFarmShrimpPrice', true);
      const farmShrimpPrices = await createAxios().get(`/farm_shrimp_prices`, {
        params: { farm_id__in: self.farmId, per_page: 1, sort: 'shrimp_price.date,desc|updated_at,desc' }
      }).then((response) => {
        const { data: { data } } = response
        return data;
      }).catch((e) => {
        console.warn(e)
        return []
      })
      if (farmShrimpPrices.length) this.set('farmShrimpPriceId', farmShrimpPrices[0].id);
      else this.set('farmShrimpPriceId', null)
      this.set('farmShrimpPrices', farmShrimpPrices);
      this.set('queryingFarmShrimpPrice', false);
    },
    async getPondsExtended() {
      this.set('queryingProPond', true);
      this.set('queryingRfidable', true);
      await createAxios().get(`/ponds`, {
        params: {
          farm_id__in: self.farmId,
          with: 'rfidables',
          sort: 'name_alpha_numeric',
          per_page: 1000
        }
      }).then((response) => {
        const { data: { data } } = response
        this.set('proPonds', data.filter((d) => d.pro));
        this.set('rfidables', _.orderBy(_.flatMap(data, (d) => d.rfidables), ['created_at'], ['desc']));
        return data;
      }).catch((e) => {
        console.warn(e)
        return false
      })
      this.set('queryingProPond', false);
      this.set('queryingRfidable', false);
    },
    async getStocks(sync = true, onFinishSync = undefined) {
      return self.getModels(
        Stock,
        'queryingStock', 'syncingStock', 'syncedStock',
        { farm_id__eq: self.farmId, sort: 'product_name' },
        (models) => this.set("stocks", models.map((model: Stock) => model.toArray())),
        sync, async () => this.syncStocks(), onFinishSync
      )
    },
    async syncStocks() {
      if (Platform.OS === 'web') return true;

      this.set('syncingStock', true)
      await Product.syncAll();
      Stock.endpoint = `/farms/${self.farmId}/stocks`
      const success = await Stock.syncAll(
        { farm_id__in: self.farmId },
        [['stocks.farm_id', [self.farmId]]], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )
      Stock.endpoint = `/stocks`

      this.set('syncingStock', false)
      return success
    },
    init(onFinishSync = undefined) {
      this.set('initiating', true)
      this.getPonds(true, () => {
        this.getCycles(true, () => {
          this.getBatches(true, () => {
            if (onFinishSync) onFinishSync()
            this.set('initiating', false)
          }).catch()
        }).catch()
      }).catch()
      this.getUsers(true).catch()
    },
    initWithLastBatch(onFinishSync = undefined) {
      this.init(() => {
        if (self.lastBatchStore) self.lastBatchStore.init()
        if (onFinishSync) onFinishSync()
        this.set('initiating', false)
      })
      this.getUsers(true).catch()
    },
    initWithLastBatchAndWaterQualityMultiMeasurements(onFinishSync = undefined) {
      this.initWithLastBatch(() => {
        this.getWaterQualityMultiMeasurements().catch()
        if (onFinishSync) onFinishSync()
        this.set('initiating', false)
      })
      this.getUsers(true).catch()
    },
    initialInit(onFinish = undefined) {
      this.set('initiating', true)
      this.getPonds(false).then(() => {
        this.set('syncedPond', true);
        this.getCycles(false).then(() => {
          this.set('syncedCycle', true);
          self.pondStoreModels.forEach((pondStore) => {
            pondStore.set('syncedCycle', true);
          })
          this.getBatches(false).then(() => {
            this.set('syncedBatch', true);
            if (onFinish) onFinish()
            this.set('initiating', false)
          }).catch()
          this.set('initiating', false)
        }).catch()
      }).catch()
    },
    initialInitWithLastBatch(onFinish = undefined) {
      this.initialInit(() => {
        if (self.lastBatchStore) self.lastBatchStore.init()
        if (onFinish) onFinish()
        this.set('initiating', false)
      })
    },
    initialInitWithLastBatchAndWaterQualityMultiMeasurements(onFinish = undefined) {
      this.initialInitWithLastBatch(() => {
        this.getWaterQualityMultiMeasurements().catch()
        if (onFinish) onFinish()
        this.set('initiating', false);
      });
    },
  })) // eslint-disable-line @typescript-eslint/no-unused-vars

type FarmStoreType = Instance<typeof FarmStoreModel>
export interface FarmStore extends FarmStoreType {}
type FarmStoreSnapshotType = SnapshotOut<typeof FarmStoreModel>
export interface FarmStoreSnapshot extends FarmStoreSnapshotType {}
export const createFarmStoreDefaultModel = () => types.optional(FarmStoreModel, {})
